import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export type SearchableFilterOption = { value: string; label: string };

type SearchableFilterSelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableFilterOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  triggerClassName?: string;
  emptyMessage?: string;
  disabled?: boolean;
  /** When set with `onOpenSelectIdChange`, only one filter menu stays open. */
  selectId?: string;
  openSelectId?: string | null;
  onOpenSelectIdChange?: (id: string | null) => void;
};

const OPTION_HEIGHT_PX = 32;
const SEARCH_BAR_HEIGHT_PX = 40;
const MENU_PADDING_PX = 8;
const MAX_VISIBLE = 8;

export function SearchableFilterSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  triggerClassName = "",
  emptyMessage = "No options available",
  disabled = false,
  selectId,
  openSelectId,
  onOpenSelectIdChange,
}: SearchableFilterSelectProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  const isControlled =
    selectId != null && onOpenSelectIdChange != null;
  const open = isControlled ? openSelectId === selectId : internalOpen;

  const setOpen = (next: boolean) => {
    if (disabled) return;
    if (isControlled) {
      onOpenSelectIdChange!(next ? selectId! : null);
      return;
    }
    setInternalOpen(next);
  };

  const selectedLabel = useMemo(() => {
    return options.find(option => option.value === value)?.label ?? "";
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;
    return options.filter(option => option.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  const menuMaxHeight =
    SEARCH_BAR_HEIGHT_PX +
    Math.max(Math.min(filteredOptions.length || 1, MAX_VISIBLE), 1) *
      OPTION_HEIGHT_PX +
    MENU_PADDING_PX;

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportPadding = 8;
    const maxHeight = Math.min(
      menuMaxHeight,
      window.innerHeight - rect.bottom - viewportPadding
    );

    setMenuStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 260),
      maxHeight,
      zIndex: 2147483647,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, filteredOptions.length, menuMaxHeight]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      return;
    }
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", handleOutside);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [open]);

  const menu = open ? (
    <div
      ref={menuRef}
      className="overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md"
      style={menuStyle}
    >
      <div className="border-b border-border p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            placeholder={searchPlaceholder}
            className="h-8 pl-8 text-sm"
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      <ul className="max-h-64 overflow-y-auto p-1" role="listbox">
        {options.length === 0 ? (
          <li className="px-2 py-2 text-sm text-muted-foreground">
            {emptyMessage}
          </li>
        ) : filteredOptions.length === 0 ? (
          <li className="px-2 py-2 text-sm text-muted-foreground">
            No results found
          </li>
        ) : (
          filteredOptions.map(option => (
            <li key={option.value} role="option" aria-selected={option.value === value}>
              <button
                type="button"
                className={`flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground ${
                  option.value === value ? "bg-accent/70 font-medium" : ""
                }`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  onValueChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex h-9 items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-input bg-background px-3 text-sm shadow-sm ring-offset-background transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${triggerClassName}`}
        onMouseDown={e => e.preventDefault()}
        onClick={() => setOpen(!open)}
      >
        <span
          className={`min-w-0 truncate ${selectedLabel ? "text-foreground" : "text-muted-foreground"}`}
        >
          {selectedLabel || placeholder}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>
      {menu && createPortal(menu, document.body)}
    </>
  );
}
